/**
 * `CustomerVectorStoreModel` — port of Unity's
 * `Models/StorageModel/CustomerVectorStoreModel.cs` (1071 lines). See
 * {@link ICustomerVectorStoreModel} for the contract and the shared-store
 * rationale.
 *
 * **Transport.** Unity talks to `https://{AISvcHost}/v1` directly with a
 * bearer header. A browser cannot: the AI host sends no CORS headers, and the
 * key must never reach the bundle. Every call therefore rides the same
 * same-origin proxy the Workflow Builder already uses (`aiProxyFetch`), which
 * rewrites `/__campfire_ai_responses/*` → `https://{aisvc_host}/*` and injects
 * `Authorization` server-side. Verified live: the proxy forwards
 * `/v1/vector_stores` and `/v1/files` unchanged.
 *
 * **Deliberate deviations, each with its reason:**
 *  - No `SemaphoreSlim`; the create path is serialized by caching the
 *    in-flight promise per customer, which is the JS equivalent and also
 *    dedupes concurrent readers.
 *  - Unity swallows a failed store LIST as `null` and then creates a second
 *    store. Here a failed list is distinguished from an empty one, so a
 *    transient error can never fork the account onto a new, empty store —
 *    that would silently hide every already-indexed document.
 *  - `WaitForVectorStoreFileCompletion` keeps Unity's 20 × 1500 ms budget but
 *    honours the caller's `AbortSignal` at every tick, because on web the tab
 *    can navigate away mid-upload.
 */

import { getAiConfig, isAiEnabled } from "@ai/aiConfig";
import { aiProxyFetch } from "@ai/subagents/aiResponsesUtility";
import { VECTOR_STORE_SETTINGS_STORAGE_PATH } from "@models/data/docsManifestDto";
import {
  normalizeProductNameKey,
  sameNormalizedNameKey,
} from "@models/data/productDocumentDto";
import type { IStorageModel } from "@models/storage/IStorageModel";
import type {
  FileSearchTool,
  ICustomerVectorStoreModel,
  ProductDocumentVectorSource,
  ProductDocumentVectorSyncResult,
  WorkflowDocumentVectorSource,
} from "./ICustomerVectorStoreModel";
import { K_RETRIEVAL_SCORE_FLOOR } from "./ICustomerVectorStoreModel";

/** Unity `VectorStoreSettings` (`:78-84`) — PascalCase JSON, shared with Unity. */
interface VectorStoreSettings {
  Version?: number;
  CustomerId?: string;
  VectorStoreId?: string;
}

interface VectorStoreFileRecord {
  id: string;
  fileName: string | null;
  storagePath: string | null;
  productKey: string | null;
  workflowId: string | null;
  knowledgeKey: string | null;
  sourceModifiedMs: string | null;
  sourceSize: string | null;
}

interface WorkflowVectorManifest {
  version?: number;
  customerId?: string;
  workflowId?: string;
  files?: unknown;
}

interface VectorStoreFileInfo {
  id: string | null;
  status: string | null;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
}

/** Unity's `OpenAI-Beta` header, sent on every vector-store call. */
const K_BETA_HEADER = { "OpenAI-Beta": "assistants=v2" } as const;

const K_LIST_TIMEOUT_MS = 30_000;
const K_GET_TIMEOUT_MS = 20_000;
const K_CREATE_TIMEOUT_MS = 30_000;
const K_UPLOAD_TIMEOUT_MS = 120_000;
const K_ATTACH_TIMEOUT_MS = 60_000;
const K_DELETE_TIMEOUT_MS = 30_000;

/** Unity `maxAttempts = 20`, `delayMs = 1500` (`:870-871`). */
const K_POLL_ATTEMPTS = 20;
const K_POLL_DELAY_MS = 1500;

/** Unity's bounded list scans (`:567-568`, `:749-750`). */
const K_STORE_PAGE_SIZE = 100;
const K_STORE_MAX_PAGES = 10;
const K_FILE_PAGE_SIZE = 100;
const K_FILE_MAX_PAGES = 20;

export class CustomerVectorStoreModel implements ICustomerVectorStoreModel {
  private m_cachedCustomerId: string | null = null;
  private m_cachedVectorStoreId: string | null = null;
  /** Serializes concurrent resolution (the web spelling of Unity's gate). */
  private m_resolve: Promise<string | null> | null = null;
  private m_resolveCustomerId: string | null = null;
  private readonly m_productSyncs = new Map<string, Promise<ProductDocumentVectorSyncResult>>();
  private readonly m_workflowSyncs = new Map<string, Promise<ProductDocumentVectorSyncResult>>();

  // No auth dependency: every entry point takes an explicit `customerId`,
  // because a cross-account shared project's documents live in the OWNING
  // account's vector store, not the signed-in user's.
  constructor(private readonly m_storage: IStorageModel) {}

  reset(): void {
    this.m_cachedCustomerId = null;
    this.m_cachedVectorStoreId = null;
    this.m_resolve = null;
    this.m_resolveCustomerId = null;
    this.m_productSyncs.clear();
    this.m_workflowSyncs.clear();
  }

  async getOrCreateVectorStoreIdAsync(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!customerId?.trim() || !isAiEnabled() || !getAiConfig().responsesAuthAvailable) {
      return null;
    }
    if (this.m_cachedCustomerId === customerId && this.m_cachedVectorStoreId) {
      return this.m_cachedVectorStoreId;
    }
    if (this.m_resolve && this.m_resolveCustomerId === customerId) return this.m_resolve;

    this.m_resolveCustomerId = customerId;
    this.m_resolve = this.resolveVectorStoreIdAsync(customerId, signal).finally(() => {
      this.m_resolve = null;
      this.m_resolveCustomerId = null;
    });
    return this.m_resolve;
  }

  private async resolveVectorStoreIdAsync(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    // 1) Persisted mapping (may be stale).
    const persistedId = await this.tryLoadVectorStoreIdAsync(customerId, signal);

    // 2) Unity ALWAYS scans for the most recent store named `customerId` and
    //    prefers it, so a stale persisted id can't pin the client to a store
    //    that was deleted or superseded.
    const scan = await this.findMostRecentVectorStoreIdAsync(customerId, signal);
    if (scan.failed) {
      // DEVIATION: Unity returns null here and falls through to CREATE, which
      // forks the account onto a fresh empty store on any transient error.
      // Prefer the persisted id; if there isn't one, give up for this call.
      if (persistedId) {
        this.cache(customerId, persistedId);
        return persistedId;
      }
      console.warn(
        "[CustomerVectorStoreModel] vector-store list failed and no id is persisted; skipping create",
      );
      return null;
    }

    if (scan.id) {
      if (scan.id !== persistedId) {
        console.info(
          `[CustomerVectorStoreModel] using vector store ${scan.id} for customer ${customerId} (was: ${persistedId ?? "none"})`,
        );
        await this.saveVectorStoreIdAsync(customerId, scan.id, signal);
      }
      this.cache(customerId, scan.id);
      return scan.id;
    }

    // 3) Nothing found by name — verify the persisted id still exists.
    if (persistedId) {
      if (await this.vectorStoreExistsAsync(persistedId, signal)) {
        this.cache(customerId, persistedId);
        return persistedId;
      }
      console.warn(
        `[CustomerVectorStoreModel] persisted vector store ${persistedId} no longer exists for customer ${customerId}`,
      );
    }

    // 4) Create + persist.
    const createdId = await this.createVectorStoreAsync(customerId, signal);
    if (!createdId) return null;
    await this.saveVectorStoreIdAsync(customerId, createdId, signal);
    this.cache(customerId, createdId);
    return createdId;
  }

  private async resolveExistingVectorStoreIdAsync(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!customerId?.trim() || !isAiEnabled() || !getAiConfig().responsesAuthAvailable) {
      return null;
    }
    if (this.m_cachedCustomerId === customerId && this.m_cachedVectorStoreId) {
      return this.m_cachedVectorStoreId;
    }

    const persistedId = await this.tryLoadVectorStoreIdAsync(customerId, signal);
    const scan = await this.findMostRecentVectorStoreIdAsync(customerId, signal);
    if (scan.failed) {
      if (persistedId) {
        this.cache(customerId, persistedId);
        return persistedId;
      }
      return null;
    }

    if (scan.id) {
      if (scan.id !== persistedId) await this.saveVectorStoreIdAsync(customerId, scan.id, signal);
      this.cache(customerId, scan.id);
      return scan.id;
    }

    if (persistedId && await this.vectorStoreExistsAsync(persistedId, signal)) {
      this.cache(customerId, persistedId);
      return persistedId;
    }
    return null;
  }

  async tryBuildProductFileSearchToolAsync(
    customerId: string,
    productKey: string,
    restrictToFileName?: string | null,
    maxNumResults = 6,
    signal?: AbortSignal,
  ): Promise<FileSearchTool | null> {
    const product = normalizeProductNameKey(productKey);
    if (!customerId?.trim() || !product) return null;
    try {
      const vectorStoreId = await this.getOrCreateVectorStoreIdAsync(customerId, signal);
      if (!vectorStoreId) return null;

      const filters: Record<string, unknown>[] = [buildEqFilter("product", product)];
      const restrict = restrictToFileName?.trim();
      if (restrict) filters.push(buildEqFilter("filename", restrict));

      return {
        type: "file_search",
        vector_store_ids: [vectorStoreId.trim()],
        max_num_results: maxNumResults,
        // The retrieval floor — see `K_RETRIEVAL_SCORE_FLOOR`. Verified
        // accepted by this endpoint through this proxy on 2026-08-28: an
        // unknown tool field is rejected outright (`unknown_parameter`), while
        // this one is parsed AND range-validated ("Expected a value <= 1.0"),
        // so it cannot be silently ignored. The transport retries once without
        // it if that ever regresses.
        ranking_options: { score_threshold: K_RETRIEVAL_SCORE_FLOOR },
        filters:
          filters.length === 1
            ? filters[0]
            : { type: "and", filters },
      };
    } catch (err) {
      console.warn("[CustomerVectorStoreModel] failed to build product file_search tool:", err);
      return null;
    }
  }

  async tryBuildWorkflowKnowledgeFileSearchToolAsync(
    customerId: string,
    args: {
      readonly productKey?: string | null;
      readonly workflowId?: string | null;
      readonly restrictToFileName?: string | null;
      readonly maxNumResults?: number;
    },
    signal?: AbortSignal,
  ): Promise<FileSearchTool | null> {
    const product = normalizeProductNameKey(args.productKey);
    const workflowId = args.workflowId?.trim() ?? "";
    if (!customerId?.trim() || (!product && !workflowId)) return null;
    try {
      const vectorStoreId = await this.getOrCreateVectorStoreIdAsync(customerId, signal);
      if (!vectorStoreId) return null;

      // A loaded workflow is the narrowest knowledge scope. Product-only search
      // remains for projects without a workflow id, but must not broaden a
      // workflow question back out to every product document.
      const filters: Record<string, unknown>[] = [
        workflowId ? buildEqFilter("workflow_id", workflowId) : buildEqFilter("product", product),
      ];
      const restrict = args.restrictToFileName?.trim();
      if (restrict) filters.push(buildEqFilter("filename", restrict));

      return {
        type: "file_search",
        vector_store_ids: [vectorStoreId.trim()],
        max_num_results: args.maxNumResults ?? 6,
        ranking_options: { score_threshold: K_RETRIEVAL_SCORE_FLOOR },
        filters:
          filters.length === 1
            ? filters[0]
            : { type: "and", filters },
      };
    } catch (err) {
      console.warn("[CustomerVectorStoreModel] failed to build workflow knowledge file_search tool:", err);
      return null;
    }
  }

  async syncProductDocumentUploadAsync(
    customerId: string,
    productKey: string,
    bytes: Uint8Array,
    fileName: string,
    signal?: AbortSignal,
  ): Promise<boolean> {
    if (!isAiEnabled() || !getAiConfig().responsesAuthAvailable) return false;
    const product = normalizeProductNameKey(productKey);
    if (!customerId?.trim() || !product || !fileName?.trim() || bytes.byteLength === 0) return false;

    const vectorStoreId = await this.getOrCreateVectorStoreIdAsync(customerId, signal);
    if (!vectorStoreId) return false;

    const openAiFileId = await this.uploadFileAsync(bytes, fileName, signal);
    if (!openAiFileId) return false;

    // Future product-doc retrieval contract. `storage_path` is derived from the
    // file name and always `docs/{fileName}`, matching the library's own key
    // derivation.
    const attributes: Record<string, string> = {};
    attributes.product = product;
    attributes.filename = fileName;
    attributes.storage_path = `docs/${fileName}`;

    const attached = await this.attachFileAsync(vectorStoreId, openAiFileId, attributes, signal);
    if (!attached?.id) {
      await this.deleteOpenAiFileAsync(openAiFileId, signal);
      return false;
    }

    if (isFailedStatus(attached.status)) {
      logFileFailure(fileName, attached);
      await this.deleteVectorStoreFileAsync(vectorStoreId, attached.id, signal);
      await this.deleteOpenAiFileAsync(openAiFileId, signal);
      return false;
    }
    if (isCompletedStatus(attached.status)) return true;

    // Wait for indexing so a caller never reports success for a doc that is
    // not yet searchable.
    const completed = await this.waitForCompletionAsync(vectorStoreId, attached.id, signal);
    if (!completed) {
      console.warn(
        `[CustomerVectorStoreModel] unable to verify vector store file status for ${fileName}`,
      );
      return true; // Unity parity: unverified is not a failure.
    }
    if (isFailedStatus(completed.status)) {
      logFileFailure(fileName, completed);
      await this.deleteVectorStoreFileAsync(vectorStoreId, completed.id ?? attached.id, signal);
      await this.deleteOpenAiFileAsync(openAiFileId, signal);
      return false;
    }
    return true;
  }

  async syncProductDocumentsAsync(
    customerId: string,
    productKey: string,
    documents: ReadonlyArray<ProductDocumentVectorSource>,
    signal?: AbortSignal,
  ): Promise<ProductDocumentVectorSyncResult> {
    const product = normalizeProductNameKey(productKey);
    const sources = normalizeVectorSources(documents);
    const skipped = (reason: string): ProductDocumentVectorSyncResult => ({
      skipped: true,
      reason,
      checked: sources.length,
      current: 0,
      uploaded: 0,
      deleted: 0,
      failed: 0,
    });
    if (!isAiEnabled()) return skipped("ai_disabled");
    if (!getAiConfig().responsesAuthAvailable) return skipped("responses_auth_unavailable");
    if (!customerId?.trim() || !product) return skipped("missing_context");

    const key = productSyncKey(customerId, product, sources);
    const inFlight = this.m_productSyncs.get(key);
    if (inFlight) return inFlight;

    const task = this.syncProductDocumentsCoreAsync(customerId, product, sources, signal).finally(() => {
      this.m_productSyncs.delete(key);
    });
    this.m_productSyncs.set(key, task);
    return task;
  }

  async syncWorkflowDocumentsAsync(
    customerId: string,
    workflowId: string,
    documents: ReadonlyArray<WorkflowDocumentVectorSource>,
    signal?: AbortSignal,
  ): Promise<ProductDocumentVectorSyncResult> {
    const workflow = workflowId?.trim();
    const sources = normalizeWorkflowVectorSources(documents);
    const skipped = (reason: string): ProductDocumentVectorSyncResult => ({
      skipped: true,
      reason,
      checked: sources.length,
      current: 0,
      uploaded: 0,
      deleted: 0,
      failed: 0,
    });
    if (!isAiEnabled()) return skipped("ai_disabled");
    if (!getAiConfig().responsesAuthAvailable) return skipped("responses_auth_unavailable");
    if (!customerId?.trim() || !workflow) return skipped("missing_context");

    const key = workflowSyncKey(customerId, workflow, sources);
    const inFlight = this.m_workflowSyncs.get(key);
    if (inFlight) return inFlight;

    const task = this.syncWorkflowDocumentsCoreAsync(customerId, workflow, sources, signal).finally(() => {
      this.m_workflowSyncs.delete(key);
    });
    this.m_workflowSyncs.set(key, task);
    return task;
  }

  private async syncProductDocumentsCoreAsync(
    customerId: string,
    productKey: string,
    documents: ReadonlyArray<ProductDocumentVectorSource>,
    signal?: AbortSignal,
  ): Promise<ProductDocumentVectorSyncResult> {
    const result = {
      skipped: false,
      checked: documents.length,
      current: 0,
      uploaded: 0,
      deleted: 0,
      failed: 0,
    };
    const vectorStoreId =
      documents.length > 0
        ? await this.getOrCreateVectorStoreIdAsync(customerId, signal)
        : await this.resolveExistingVectorStoreIdAsync(customerId, signal);
    if (!vectorStoreId) return { ...result, skipped: true, reason: "vector_store_unavailable" };

    const attachedFiles = await this.listVectorStoreFilesAsync(vectorStoreId, signal);
    if (attachedFiles === null) return { ...result, skipped: true, reason: "vector_file_list_failed" };

    const deletedIds = new Set<string>();
    for (const record of attachedFiles) {
      if (!doesFileMatchProduct(record, productKey)) continue;
      const stillLinked = documents.some((document) => (
        doesFileMatch(record, productKey, document.storagePath, document.fileName)
      ));
      if (stillLinked) continue;

      if (await this.deleteVectorStoreAndSourceFileAsync(vectorStoreId, record.id, signal)) {
        deletedIds.add(record.id);
        result.deleted += 1;
      } else {
        result.failed += 1;
      }
    }

    for (const document of documents) {
      if (signal?.aborted) return { ...result, skipped: true, reason: "aborted" };

      const matching = attachedFiles.filter((record) => (
        !deletedIds.has(record.id)
        && doesFileMatch(record, productKey, document.storagePath, document.fileName)
      ));
      // Same size/mtime with mixed-case `product` metadata is not current:
      // file_search `eq` is case-sensitive, so re-attach with the canonical key.
      const current = matching.filter((record) => (
        isVectorFileCurrent(record, document)
        && record.productKey === productKey
      ));
      if (current.length > 0) {
        result.current += 1;
        for (const duplicate of current.slice(1)) {
          if (await this.deleteVectorStoreAndSourceFileAsync(vectorStoreId, duplicate.id, signal)) {
            deletedIds.add(duplicate.id);
            result.deleted += 1;
          } else {
            result.failed += 1;
          }
        }
        for (const stale of matching.filter((record) => !current.includes(record))) {
          if (await this.deleteVectorStoreAndSourceFileAsync(vectorStoreId, stale.id, signal)) {
            deletedIds.add(stale.id);
            result.deleted += 1;
          } else {
            result.failed += 1;
          }
        }
        continue;
      }

      for (const stale of matching) {
        if (await this.deleteVectorStoreAndSourceFileAsync(vectorStoreId, stale.id, signal)) {
          deletedIds.add(stale.id);
          result.deleted += 1;
        } else {
          result.failed += 1;
        }
      }

      const source = await this.m_storage.getDocBytes(customerId, document.storagePath, signal);
      if (!source?.bytes.byteLength) {
        console.warn(
          `[CustomerVectorStoreModel] product document bytes unavailable for ${document.storagePath}`,
        );
        result.failed += 1;
        continue;
      }

      const uploaded = await this.uploadAndAttachProductDocumentAsync(
        vectorStoreId,
        productKey,
        source.bytes,
        document,
        signal,
      );
      if (uploaded) result.uploaded += 1;
      else result.failed += 1;
    }

    return result;
  }

  private async syncWorkflowDocumentsCoreAsync(
    customerId: string,
    workflowId: string,
    documents: ReadonlyArray<WorkflowDocumentVectorSource>,
    signal?: AbortSignal,
  ): Promise<ProductDocumentVectorSyncResult> {
    const result = {
      skipped: false,
      checked: documents.length,
      current: 0,
      uploaded: 0,
      deleted: 0,
      failed: 0,
    };
    const vectorStoreId =
      documents.length > 0
        ? await this.getOrCreateVectorStoreIdAsync(customerId, signal)
        : await this.resolveExistingVectorStoreIdAsync(customerId, signal);
    if (!vectorStoreId) return { ...result, skipped: true, reason: "vector_store_unavailable" };

    const manifestFiles = await this.loadWorkflowVectorManifestAsync(customerId, workflowId, signal);
    if (manifestFiles === null) return { ...result, skipped: true, reason: "workflow_vector_manifest_failed" };
    const attachedFiles = manifestFiles.filter((record) => doesFileMatchWorkflow(record, workflowId));

    const deletedIds = new Set<string>();
    for (const record of attachedFiles) {
      if (!doesFileMatchWorkflow(record, workflowId)) continue;
      const stillLinked = documents.some((document) => (
        doesFileMatchIdentity(record, document.storagePath, document.fileName)
        && sameNormalizedNameKey(record.knowledgeKey, document.knowledgeKey)
      ));
      if (stillLinked) continue;

      if (await this.deleteVectorStoreAndSourceFileAsync(vectorStoreId, record.id, signal)) {
        deletedIds.add(record.id);
        result.deleted += 1;
      } else {
        result.failed += 1;
      }
    }

    for (const document of documents) {
      if (signal?.aborted) return { ...result, skipped: true, reason: "aborted" };

      const matching = attachedFiles.filter((record) => (
        !deletedIds.has(record.id)
        && doesFileMatchWorkflow(record, workflowId)
        && doesFileMatchIdentity(record, document.storagePath, document.fileName)
        && sameNormalizedNameKey(record.knowledgeKey, document.knowledgeKey)
      ));
      const current = matching.filter((record) => (
        isVectorFileCurrent(record, document)
        && record.knowledgeKey === document.knowledgeKey
      ));
      if (current.length > 0) {
        result.current += 1;
        for (const duplicate of current.slice(1)) {
          if (await this.deleteVectorStoreAndSourceFileAsync(vectorStoreId, duplicate.id, signal)) {
            deletedIds.add(duplicate.id);
            result.deleted += 1;
          } else {
            result.failed += 1;
          }
        }
        for (const stale of matching.filter((record) => !current.includes(record))) {
          if (await this.deleteVectorStoreAndSourceFileAsync(vectorStoreId, stale.id, signal)) {
            deletedIds.add(stale.id);
            result.deleted += 1;
          } else {
            result.failed += 1;
          }
        }
        continue;
      }

      for (const stale of matching) {
        if (await this.deleteVectorStoreAndSourceFileAsync(vectorStoreId, stale.id, signal)) {
          deletedIds.add(stale.id);
          result.deleted += 1;
        } else {
          result.failed += 1;
        }
      }

      const source = await this.m_storage.getDocBytes(customerId, document.storagePath, signal);
      if (!source?.bytes.byteLength) {
        console.warn(
          `[CustomerVectorStoreModel] workflow document bytes unavailable for ${document.storagePath}`,
        );
        result.failed += 1;
        continue;
      }

      const uploaded = await this.uploadAndAttachWorkflowDocumentAsync(
        vectorStoreId,
        workflowId,
        source.bytes,
        document,
        signal,
      );
      if (uploaded) {
        result.uploaded += 1;
        attachedFiles.push(uploaded);
      } else {
        result.failed += 1;
      }
    }

    await this.saveWorkflowVectorManifestAsync(
      customerId,
      workflowId,
      attachedFiles.filter((record) => !deletedIds.has(record.id)),
      signal,
    );
    return result;
  }

  async syncProductDocumentDeletionAsync(
    customerId: string,
    productKey: string,
    storagePath: string,
    fileName: string,
    signal?: AbortSignal,
  ): Promise<boolean> {
    if (!isAiEnabled() || !getAiConfig().responsesAuthAvailable) return false;
    const product = normalizeProductNameKey(productKey);
    if (!customerId?.trim() || !product) return false;

    const normalizedStoragePath = storagePath?.trim() || null;
    const normalizedFileName =
      fileName?.trim() || normalizedStoragePath?.split("/").pop() || null;
    if (!normalizedStoragePath && !normalizedFileName) return false;

    const vectorStoreId = await this.getOrCreateVectorStoreIdAsync(customerId, signal);
    if (!vectorStoreId) return false;

    const attachedFiles = await this.listVectorStoreFilesAsync(vectorStoreId, signal);
    if (attachedFiles === null) return false;

    let matchedAny = false;
    let allDeleted = true;
    for (const record of attachedFiles) {
      if (
        !doesFileMatch(
          record,
          product,
          normalizedStoragePath,
          normalizedFileName,
        )
      ) {
        continue;
      }
      matchedAny = true;
      if (!(await this.deleteVectorStoreFileAsync(vectorStoreId, record.id, signal))) {
        allDeleted = false;
        continue;
      }
      // Unity passes `attachedFile.Id` to the file delete too: a vector-store
      // file's id IS the underlying file id for files attached this way.
      if (!(await this.deleteOpenAiFileAsync(record.id, signal))) allDeleted = false;
    }
    return !matchedAny || allDeleted;
  }

  // ---- Settings blob ------------------------------------------------

  private async tryLoadVectorStoreIdAsync(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    try {
      const json = await this.m_storage.getVectorStoreSettingsJson(customerId, signal);
      if (!json?.trim()) return null;
      const settings = JSON.parse(json) as VectorStoreSettings;
      return settings?.VectorStoreId?.trim() || null;
    } catch (err) {
      console.warn(`[CustomerVectorStoreModel] failed reading ${VECTOR_STORE_SETTINGS_STORAGE_PATH}:`, err);
      return null;
    }
  }

  private async saveVectorStoreIdAsync(
    customerId: string,
    vectorStoreId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !vectorStoreId) return;
    try {
      const settings: VectorStoreSettings = {
        Version: 1,
        CustomerId: customerId,
        VectorStoreId: vectorStoreId,
      };
      await this.m_storage.putVectorStoreSettingsJson(
        customerId,
        JSON.stringify(settings),
        signal,
      );
    } catch (err) {
      console.warn("[CustomerVectorStoreModel] failed saving vector store settings:", err);
    }
  }

  private async loadWorkflowVectorManifestAsync(
    customerId: string,
    workflowId: string,
    signal?: AbortSignal,
  ): Promise<VectorStoreFileRecord[] | null> {
    try {
      const json = await this.m_storage.getWorkflowVectorManifestJson(customerId, workflowId, signal);
      if (!json?.trim()) return [];
      const parsed = JSON.parse(json) as WorkflowVectorManifest;
      if (!Array.isArray(parsed.files)) return [];
      return parsed.files.map(parseManifestFileRecord).filter((record): record is VectorStoreFileRecord => !!record);
    } catch (err) {
      console.warn(
        `[CustomerVectorStoreModel] failed reading workflow vector manifest for ${workflowId}:`,
        err,
      );
      return null;
    }
  }

  private async saveWorkflowVectorManifestAsync(
    customerId: string,
    workflowId: string,
    files: ReadonlyArray<VectorStoreFileRecord>,
    signal?: AbortSignal,
  ): Promise<void> {
    try {
      const manifest: WorkflowVectorManifest = {
        version: 1,
        customerId,
        workflowId,
        files: files.filter((record) => record.id && doesFileMatchWorkflow(record, workflowId)),
      };
      await this.m_storage.putWorkflowVectorManifestJson(
        customerId,
        workflowId,
        JSON.stringify(manifest),
        signal,
      );
    } catch (err) {
      console.warn(
        `[CustomerVectorStoreModel] failed saving workflow vector manifest for ${workflowId}:`,
        err,
      );
    }
  }

  // ---- OpenAI calls -------------------------------------------------

  private async vectorStoreExistsAsync(
    vectorStoreId: string,
    signal?: AbortSignal,
  ): Promise<boolean> {
    try {
      const res = await aiProxyFetch(
        `/v1/vector_stores/${encodeURIComponent(vectorStoreId)}`,
        { method: "GET", headers: { ...K_BETA_HEADER } },
        K_GET_TIMEOUT_MS,
        signal,
      );
      if (res.ok) return true;
      // Only a true 404 means "gone"; anything else is assumed transient
      // (Unity `:555-556`).
      return res.status !== 404;
    } catch {
      return true;
    }
  }

  /**
   * Unity `FindMostRecentVectorStoreIdByCustomerIdAsync` (`:563-656`), match
   * by `name === customerId` first, then `metadata.customer_id`. Returns
   * `failed` separately from "not found" — see the class header.
   */
  private async findMostRecentVectorStoreIdAsync(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<{ id: string | null; failed: boolean }> {
    let after: string | null = null;
    for (let page = 0; page < K_STORE_MAX_PAGES; page++) {
      let url = `/v1/vector_stores?limit=${K_STORE_PAGE_SIZE}&order=desc`;
      if (after) url += `&after=${encodeURIComponent(after)}`;

      let root: Record<string, unknown>;
      try {
        const res = await aiProxyFetch(
          url,
          { method: "GET", headers: { ...K_BETA_HEADER } },
          K_LIST_TIMEOUT_MS,
          signal,
        );
        if (!res.ok) {
          console.warn(
            `[CustomerVectorStoreModel] vector store list error: http ${res.status} ${await safeText(res)}`,
          );
          return { id: null, failed: true };
        }
        root = (await res.json()) as Record<string, unknown>;
      } catch (err) {
        console.warn("[CustomerVectorStoreModel] vector store list error:", err);
        return { id: null, failed: true };
      }

      const data = root.data;
      if (!Array.isArray(data)) return { id: null, failed: false };
      for (const item of data) {
        if (!isRecord(item)) continue;
        const id = typeof item.id === "string" ? item.id : null;
        if (!id) continue;
        if (typeof item.name === "string" && item.name === customerId) return { id, failed: false };
        const metadata = item.metadata;
        if (isRecord(metadata) && metadata.customer_id === customerId) {
          return { id, failed: false };
        }
      }

      if (root.has_more !== true) return { id: null, failed: false };
      after = typeof root.last_id === "string" ? root.last_id : null;
      if (!after) return { id: null, failed: false };
    }
    return { id: null, failed: false };
  }

  private async createVectorStoreAsync(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    try {
      const res = await aiProxyFetch(
        "/v1/vector_stores",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...K_BETA_HEADER },
          // Unity `:661-668`: the NAME is the customer id — that is what the
          // scan above matches on, and what the Unity client will find.
          body: JSON.stringify({
            name: customerId,
            metadata: { customer_id: customerId },
          }),
        },
        K_CREATE_TIMEOUT_MS,
        signal,
      );
      if (!res.ok) {
        console.warn(
          `[CustomerVectorStoreModel] vector store create error: http ${res.status} ${await safeText(res)}`,
        );
        return null;
      }
      const root = (await res.json()) as Record<string, unknown>;
      return typeof root.id === "string" ? root.id : null;
    } catch (err) {
      console.warn("[CustomerVectorStoreModel] vector store create error:", err);
      return null;
    }
  }

  private async uploadFileAsync(
    bytes: Uint8Array,
    fileName: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    try {
      const form = new FormData();
      form.append("purpose", "assistants");
      // Let the browser set the multipart boundary — setting Content-Type
      // manually omits it and the upload fails with a parse error upstream.
      form.append("file", new Blob([bytes as BlobPart]), fileName);

      const res = await aiProxyFetch(
        "/v1/files",
        { method: "POST", body: form },
        K_UPLOAD_TIMEOUT_MS,
        signal,
      );
      if (!res.ok) {
        console.warn(
          `[CustomerVectorStoreModel] upload file error: http ${res.status} ${await safeText(res)}`,
        );
        return null;
      }
      const root = (await res.json()) as Record<string, unknown>;
      return typeof root.id === "string" ? root.id : null;
    } catch (err) {
      console.warn("[CustomerVectorStoreModel] upload file error:", err);
      return null;
    }
  }

  private async attachFileAsync(
    vectorStoreId: string,
    fileId: string,
    attributes: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<VectorStoreFileInfo | null> {
    try {
      const res = await aiProxyFetch(
        `/v1/vector_stores/${encodeURIComponent(vectorStoreId)}/files`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...K_BETA_HEADER },
          body: JSON.stringify({ file_id: fileId, attributes }),
        },
        K_ATTACH_TIMEOUT_MS,
        signal,
      );
      if (!res.ok) {
        console.warn(
          `[CustomerVectorStoreModel] vector store attach error: http ${res.status} ${await safeText(res)}`,
        );
        return null;
      }
      return parseFileInfo((await res.json()) as Record<string, unknown>);
    } catch (err) {
      console.warn("[CustomerVectorStoreModel] vector store attach error:", err);
      return null;
    }
  }

  /** `null` = the listing FAILED (distinct from an empty store). */
  private async listVectorStoreFilesAsync(
    vectorStoreId: string,
    signal?: AbortSignal,
  ): Promise<VectorStoreFileRecord[] | null> {
    const results: VectorStoreFileRecord[] = [];
    let after: string | null = null;

    for (let page = 0; page < K_FILE_MAX_PAGES; page++) {
      let url = `/v1/vector_stores/${encodeURIComponent(vectorStoreId)}/files?limit=${K_FILE_PAGE_SIZE}&order=desc`;
      if (after) url += `&after=${encodeURIComponent(after)}`;

      let root: Record<string, unknown>;
      try {
        const res = await aiProxyFetch(
          url,
          { method: "GET", headers: { ...K_BETA_HEADER } },
          K_LIST_TIMEOUT_MS,
          signal,
        );
        if (!res.ok) {
          console.warn(
            `[CustomerVectorStoreModel] vector store file list error: http ${res.status} ${await safeText(res)}`,
          );
          return null;
        }
        root = (await res.json()) as Record<string, unknown>;
      } catch (err) {
        console.warn("[CustomerVectorStoreModel] vector store file list error:", err);
        return null;
      }

      const data = root.data;
      if (!Array.isArray(data)) return results;
      for (const item of data) {
        if (!isRecord(item)) continue;
        const id = typeof item.id === "string" ? item.id : null;
        if (!id) continue;
        const attributes = isRecord(item.attributes) ? item.attributes : null;
        results.push({
          id,
          fileName: readString(attributes, "filename"),
          storagePath: readString(attributes, "storage_path")?.trim() || null,
          productKey: readString(attributes, "product"),
          workflowId: readString(attributes, "workflow_id"),
          knowledgeKey: readString(attributes, "knowledge_key"),
          sourceModifiedMs: readString(attributes, "source_modified_ms"),
          sourceSize: readString(attributes, "source_size"),
        });
      }

      if (root.has_more !== true) return results;
      after = typeof root.last_id === "string" ? root.last_id : null;
      if (!after) return results;
    }
    return results;
  }

  private async uploadAndAttachProductDocumentAsync(
    vectorStoreId: string,
    productKey: string,
    bytes: Uint8Array,
    document: ProductDocumentVectorSource,
    signal?: AbortSignal,
  ): Promise<boolean> {
    const openAiFileId = await this.uploadFileAsync(bytes, document.fileName, signal);
    if (!openAiFileId) return false;

    const attached = await this.attachFileAsync(
      vectorStoreId,
      openAiFileId,
      {
        product: productKey,
        filename: document.fileName,
        storage_path: document.storagePath,
        source_modified_ms: String(document.lastModifiedMs),
        source_size: String(document.fileSize),
      },
      signal,
    );
    if (!attached?.id) {
      await this.deleteOpenAiFileAsync(openAiFileId, signal);
      return false;
    }
    if (isFailedStatus(attached.status)) {
      logFileFailure(document.fileName, attached);
      await this.deleteVectorStoreFileAsync(vectorStoreId, attached.id, signal);
      await this.deleteOpenAiFileAsync(openAiFileId, signal);
      return false;
    }
    if (isCompletedStatus(attached.status)) return true;

    const completed = await this.waitForCompletionAsync(vectorStoreId, attached.id, signal);
    if (!completed) {
      console.warn(
        `[CustomerVectorStoreModel] unable to verify vector store file status for ${document.fileName}`,
      );
      return true;
    }
    if (isFailedStatus(completed.status)) {
      logFileFailure(document.fileName, completed);
      await this.deleteVectorStoreFileAsync(vectorStoreId, completed.id ?? attached.id, signal);
      await this.deleteOpenAiFileAsync(openAiFileId, signal);
      return false;
    }
    return true;
  }

  private async uploadAndAttachWorkflowDocumentAsync(
    vectorStoreId: string,
    workflowId: string,
    bytes: Uint8Array,
    document: WorkflowDocumentVectorSource,
    signal?: AbortSignal,
  ): Promise<VectorStoreFileRecord | null> {
    const openAiFileId = await this.uploadFileAsync(bytes, document.fileName, signal);
    if (!openAiFileId) return null;

    const attached = await this.attachFileAsync(
      vectorStoreId,
      openAiFileId,
      {
        workflow_id: workflowId,
        knowledge_key: document.knowledgeKey,
        filename: document.fileName,
        storage_path: document.storagePath,
        source_modified_ms: String(document.lastModifiedMs),
        source_size: String(document.fileSize),
      },
      signal,
    );
    if (!attached?.id) {
      await this.deleteOpenAiFileAsync(openAiFileId, signal);
      return null;
    }
    if (isFailedStatus(attached.status)) {
      logFileFailure(document.fileName, attached);
      await this.deleteVectorStoreFileAsync(vectorStoreId, attached.id, signal);
      await this.deleteOpenAiFileAsync(openAiFileId, signal);
      return null;
    }
    const record = workflowRecordFromDocument(attached.id, workflowId, document);
    if (isCompletedStatus(attached.status)) return record;

    const completed = await this.waitForCompletionAsync(vectorStoreId, attached.id, signal);
    if (!completed) {
      console.warn(
        `[CustomerVectorStoreModel] unable to verify vector store file status for ${document.fileName}`,
      );
      return record;
    }
    if (isFailedStatus(completed.status)) {
      logFileFailure(document.fileName, completed);
      await this.deleteVectorStoreFileAsync(vectorStoreId, completed.id ?? attached.id, signal);
      await this.deleteOpenAiFileAsync(openAiFileId, signal);
      return null;
    }
    return record;
  }

  private async deleteVectorStoreFileAsync(
    vectorStoreId: string,
    fileId: string,
    signal?: AbortSignal,
  ): Promise<boolean> {
    try {
      const res = await aiProxyFetch(
        `/v1/vector_stores/${encodeURIComponent(vectorStoreId)}/files/${encodeURIComponent(fileId)}`,
        { method: "DELETE", headers: { ...K_BETA_HEADER } },
        K_DELETE_TIMEOUT_MS,
        signal,
      );
      if (res.ok || res.status === 404) return true;
      console.warn(
        `[CustomerVectorStoreModel] vector store delete error: http ${res.status} ${await safeText(res)}`,
      );
      return false;
    } catch (err) {
      console.warn("[CustomerVectorStoreModel] vector store delete error:", err);
      return false;
    }
  }

  private async deleteOpenAiFileAsync(
    fileId: string,
    signal?: AbortSignal,
  ): Promise<boolean> {
    if (!fileId) return false;
    try {
      const res = await aiProxyFetch(
        `/v1/files/${encodeURIComponent(fileId)}`,
        { method: "DELETE" },
        K_DELETE_TIMEOUT_MS,
        signal,
      );
      if (res.ok || res.status === 404) return true;
      console.warn(
        `[CustomerVectorStoreModel] file delete error: http ${res.status} ${await safeText(res)}`,
      );
      return false;
    } catch (err) {
      console.warn("[CustomerVectorStoreModel] file delete error:", err);
      return false;
    }
  }

  private async deleteVectorStoreAndSourceFileAsync(
    vectorStoreId: string,
    fileId: string,
    signal?: AbortSignal,
  ): Promise<boolean> {
    const vectorDeleted = await this.deleteVectorStoreFileAsync(vectorStoreId, fileId, signal);
    const fileDeleted = await this.deleteOpenAiFileAsync(fileId, signal);
    return vectorDeleted && fileDeleted;
  }

  private async waitForCompletionAsync(
    vectorStoreId: string,
    fileId: string,
    signal?: AbortSignal,
  ): Promise<VectorStoreFileInfo | null> {
    let lastInfo: VectorStoreFileInfo | null = null;
    for (let attempt = 0; attempt < K_POLL_ATTEMPTS; attempt++) {
      if (signal?.aborted) return lastInfo;
      const info = await this.tryGetFileInfoAsync(vectorStoreId, fileId, signal);
      if (info?.status) {
        lastInfo = info;
        if (isCompletedStatus(info.status) || isFailedStatus(info.status)) return info;
      }
      await delay(K_POLL_DELAY_MS, signal);
    }
    return lastInfo;
  }

  private async tryGetFileInfoAsync(
    vectorStoreId: string,
    fileId: string,
    signal?: AbortSignal,
  ): Promise<VectorStoreFileInfo | null> {
    try {
      const res = await aiProxyFetch(
        `/v1/vector_stores/${encodeURIComponent(vectorStoreId)}/files/${encodeURIComponent(fileId)}`,
        { method: "GET", headers: { ...K_BETA_HEADER } },
        K_GET_TIMEOUT_MS,
        signal,
      );
      if (!res.ok) return null;
      return parseFileInfo((await res.json()) as Record<string, unknown>);
    } catch {
      return null;
    }
  }

  private cache(customerId: string, vectorStoreId: string): void {
    this.m_cachedCustomerId = customerId;
    this.m_cachedVectorStoreId = vectorStoreId;
  }
}

// ---- Helpers --------------------------------------------------------

function buildEqFilter(key: string, value: string): Record<string, unknown> {
  return { type: "eq", key, value };
}

function normalizeVectorSources(
  documents: ReadonlyArray<ProductDocumentVectorSource>,
): ReadonlyArray<ProductDocumentVectorSource> {
  const byStoragePath = new Map<string, ProductDocumentVectorSource>();
  for (const document of documents) {
    const storagePath = document.storagePath?.trim();
    const fileName = document.fileName?.trim() || storagePath?.split("/").pop() || "";
    if (!storagePath || !fileName) continue;
    byStoragePath.set(storagePath.toLowerCase(), {
      storagePath,
      fileName,
      fileSize: Math.max(0, Math.round(document.fileSize || 0)),
      lastModifiedMs: Math.max(0, Math.round(document.lastModifiedMs || 0)),
    });
  }
  return Array.from(byStoragePath.values()).sort((a, b) => a.storagePath.localeCompare(b.storagePath));
}

function normalizeWorkflowVectorSources(
  documents: ReadonlyArray<WorkflowDocumentVectorSource>,
): ReadonlyArray<WorkflowDocumentVectorSource> {
  const byKey = new Map<string, WorkflowDocumentVectorSource>();
  for (const document of documents) {
    const storagePath = document.storagePath?.trim();
    const fileName = document.fileName?.trim() || storagePath?.split("/").pop() || "";
    const knowledgeKey = normalizeProductNameKey(document.knowledgeKey);
    if (!storagePath || !fileName || !knowledgeKey) continue;
    byKey.set(`${knowledgeKey}\n${storagePath.toLowerCase()}`, {
      storagePath,
      fileName,
      knowledgeKey,
      fileSize: Math.max(0, Math.round(document.fileSize || 0)),
      lastModifiedMs: Math.max(0, Math.round(document.lastModifiedMs || 0)),
    });
  }
  return Array.from(byKey.values()).sort((a, b) => (
    a.knowledgeKey.localeCompare(b.knowledgeKey) || a.storagePath.localeCompare(b.storagePath)
  ));
}

function productSyncKey(
  customerId: string,
  productKey: string,
  documents: ReadonlyArray<ProductDocumentVectorSource>,
): string {
  return JSON.stringify([
    customerId,
    productKey,
    documents.map((document) => [
      document.storagePath,
      document.fileName,
      document.fileSize,
      document.lastModifiedMs,
    ]),
  ]);
}

function workflowSyncKey(
  customerId: string,
  workflowId: string,
  documents: ReadonlyArray<WorkflowDocumentVectorSource>,
): string {
  return JSON.stringify([
    customerId,
    workflowId,
    documents.map((document) => [
      document.knowledgeKey,
      document.storagePath,
      document.fileName,
      document.fileSize,
      document.lastModifiedMs,
    ]),
  ]);
}

function isVectorFileCurrent(
  record: VectorStoreFileRecord,
  document: ProductDocumentVectorSource,
): boolean {
  return (
    record.sourceModifiedMs === String(document.lastModifiedMs)
    && record.sourceSize === String(document.fileSize)
  );
}

function workflowRecordFromDocument(
  id: string,
  workflowId: string,
  document: WorkflowDocumentVectorSource,
): VectorStoreFileRecord {
  return {
    id,
    fileName: document.fileName,
    storagePath: document.storagePath,
    productKey: null,
    workflowId,
    knowledgeKey: document.knowledgeKey,
    sourceModifiedMs: String(document.lastModifiedMs),
    sourceSize: String(document.fileSize),
  };
}

function parseManifestFileRecord(value: unknown): VectorStoreFileRecord | null {
  if (!isRecord(value)) return null;
  const id = readString(value, "id")?.trim();
  if (!id) return null;
  return {
    id,
    fileName: readString(value, "fileName"),
    storagePath: readString(value, "storagePath")?.trim() || null,
    productKey: readString(value, "productKey"),
    workflowId: readString(value, "workflowId"),
    knowledgeKey: readString(value, "knowledgeKey"),
    sourceModifiedMs: readString(value, "sourceModifiedMs"),
    sourceSize: readString(value, "sourceSize"),
  };
}

function doesFileMatch(
  record: VectorStoreFileRecord,
  productKey: string | null,
  storagePath: string | null,
  fileName: string | null,
): boolean {
  if (!record?.id) return false;

  if (!sameNormalizedNameKey(record.productKey, productKey)) return false;

  const storagePathMatches = !!storagePath && equalsIgnoreCase(record.storagePath, storagePath);
  const fileNameMatches = !!fileName && equalsIgnoreCase(record.fileName, fileName);

  // When a storage path is supplied it wins, EXCEPT for legacy records that
  // carry no `storage_path` attribute at all — those fall back to the name.
  if (storagePath) return storagePathMatches || (!record.storagePath && fileNameMatches);
  return fileNameMatches;
}

function doesFileMatchIdentity(
  record: VectorStoreFileRecord,
  storagePath: string | null,
  fileName: string | null,
): boolean {
  if (!record?.id) return false;

  const storagePathMatches = !!storagePath && equalsIgnoreCase(record.storagePath, storagePath);
  const fileNameMatches = !!fileName && equalsIgnoreCase(record.fileName, fileName);

  if (storagePath) return storagePathMatches || (!record.storagePath && fileNameMatches);
  return fileNameMatches;
}

function doesFileMatchProduct(
  record: VectorStoreFileRecord,
  productKey: string | null,
): boolean {
  return !!record?.id && sameNormalizedNameKey(record.productKey, productKey);
}

function doesFileMatchWorkflow(
  record: VectorStoreFileRecord,
  workflowId: string | null,
): boolean {
  return !!record?.id && !!workflowId?.trim() && equalsIgnoreCase(record.workflowId, workflowId);
}

function equalsIgnoreCase(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function isCompletedStatus(status: string | null): boolean {
  return status?.toLowerCase() === "completed";
}

function isFailedStatus(status: string | null): boolean {
  const normalized = status?.toLowerCase();
  return normalized === "failed" || normalized === "cancelled";
}

function logFileFailure(fileName: string, info: VectorStoreFileInfo): void {
  const label = fileName || info.id || "(file)";
  const detail =
    info.lastErrorCode || info.lastErrorMessage
      ? ` error=${info.lastErrorCode}: ${info.lastErrorMessage}`
      : "";
  console.warn(
    `[CustomerVectorStoreModel] vector store indexing failed for ${label} status='${info.status}'${detail}`,
  );
}

function parseFileInfo(root: Record<string, unknown>): VectorStoreFileInfo {
  const lastError = isRecord(root.last_error) ? root.last_error : null;
  return {
    id: typeof root.id === "string" ? root.id : null,
    status: typeof root.status === "string" ? root.status : null,
    lastErrorCode: readString(lastError, "code"),
    lastErrorMessage: readString(lastError, "message"),
  };
}

function readString(source: Record<string, unknown> | null, key: string): string | null {
  if (!source) return null;
  const value = source[key];
  return typeof value === "string" ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

async function safeText(res: Response): Promise<string> {
  try {
    return (await res.text()).slice(0, 400);
  } catch {
    return "";
  }
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const handle = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(handle);
        resolve();
      },
      { once: true },
    );
  });
}
